"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import {
	AlertTriangle,
	CheckCircle,
	Copy,
	KeyRound,
	RefreshCw,
	Settings,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useAccessibility } from "@/hooks/useAccessibility";

const LENGTH_MIN = 8;
const LENGTH_MAX = 128;
const LENGTH_DEFAULT = 32;

const PasswordGenerator = () => {
	const t = useTranslations("passwordGenerator");
	const { announce } = useAccessibility();

	const [length, setLength] = useState(LENGTH_DEFAULT);
	const [encoding, setEncoding] = useState<"hex" | "base64">("hex");
	const [generatedPassword, setGeneratedPassword] = useState("");
	const [copied, setCopied] = useState(false);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	const generatePassword = useCallback(() => {
		let numBytes: number;
		if (encoding === "hex") {
			numBytes = Math.ceil(length / 2);
		} else {
			numBytes = Math.ceil((length * 3) / 4);
		}
		const bytes = new Uint8Array(numBytes);
		crypto.getRandomValues(bytes);

		let result: string;
		if (encoding === "hex") {
			result = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"))
				.join("")
				.slice(0, length);
		} else {
			result = btoa(String.fromCharCode(...bytes))
				.replace(/=/g, "")
				.slice(0, length);
		}

		setGeneratedPassword(result);
		setCopied(false);
		announce(t("a11y.passwordGenerated"));
	}, [length, encoding, announce, t]);

	const handleCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(generatedPassword);
			setCopied(true);
			announce(t("a11y.passwordCopied"));
			setTimeout(() => setCopied(false), 2000);
		} catch {
			announce(t("a11y.copyFailed"));
		}
	}, [generatedPassword, announce, t]);

	const getStatusMessage = (): string => {
		if (copied) return t("status.copied");
		if (generatedPassword) return t("status.generated");
		return t("status.ready");
	};

	if (!mounted) {
		return (
			<div className="max-w-7xl mx-auto min-h-screen p-2 sm:p-8">
				<div className="mx-auto p-2 sm:p-6">
					<header className="text-center mb-10">
						<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
							<KeyRound
								className="h-8 w-8 me-4 text-destructive"
								aria-hidden="true"
							/>
							{t("title")}
						</h1>
						<p className="mt-4 text-muted-foreground text-lg font-bold">
							{t("subtitle")}
						</p>
					</header>
				</div>
			</div>
		);
	}

	return (
		<ErrorBoundary>
			<div className="max-w-7xl mx-auto min-h-screen p-2 sm:p-8">
				<div className="mx-auto p-2 sm:p-6">
					<header className="text-center mb-10">
						<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
							<KeyRound
								className="h-8 w-8 me-4 text-destructive"
								aria-hidden="true"
							/>
							{t("title")}
						</h1>
						<p className="mt-4 text-muted-foreground text-lg font-bold">
							{t("subtitle")}
						</p>
					</header>

					{/* Status Bar */}
					<Alert
						className={`mb-6 ${generatedPassword ? "border-green-500 bg-green-50" : "border-yellow-500 bg-yellow-50"}`}
					>
						<AlertTriangle className="h-4 w-4" aria-hidden="true" />
						<AlertDescription className="font-bold">
							{getStatusMessage()}
						</AlertDescription>
					</Alert>

					<div className="flex flex-col gap-6">
						{/* Step 1: Configure */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<Settings className="w-6 h-6" aria-hidden="true" />
									{t("steps.configure")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0 space-y-6">
								<div>
									<Label
										htmlFor="length-slider"
										className="text-sm font-bold"
									>
										{t("config.lengthLabel", { value: length })}
									</Label>
									<Slider
										id="length-slider"
										value={[length]}
										onValueChange={([v]) => setLength(v)}
										min={LENGTH_MIN}
										max={LENGTH_MAX}
										step={1}
										className="mt-2"
										aria-label={t("config.lengthAriaLabel")}
									/>
								</div>

								<div>
									<Label
										htmlFor="encoding-select"
										className="text-sm font-bold"
									>
										{t("config.encodingLabel")}
									</Label>
									<Select
										value={encoding}
										onValueChange={(v) =>
											setEncoding(v as "hex" | "base64")
										}
									>
										<SelectTrigger
											id="encoding-select"
											className="w-full mt-2"
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="hex">
												{t("config.hex")}
											</SelectItem>
											<SelectItem value="base64">
												{t("config.base64")}
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</CardContent>
						</Card>

						{/* Generate Button */}
						<div className="flex justify-center">
							<Button
								onClick={generatePassword}
								size="lg"
								className="px-8 py-4 text-lg"
							>
								<RefreshCw
									className="w-5 h-5 me-3"
									aria-hidden="true"
								/>
								{generatedPassword
									? t("generate.regenerate")
									: t("generate.button")}
							</Button>
						</div>

						{/* Step 2: Result */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<KeyRound className="w-6 h-6" aria-hidden="true" />
									{t("steps.result")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								{generatedPassword ? (
									<div className="space-y-4">
										<div className="p-4 bg-muted/50 rounded-md border">
											<p
												className="font-mono text-sm break-all select-all"
												aria-live="polite"
											>
												{generatedPassword}
											</p>
										</div>
										<div className="flex justify-center">
											<Button
												onClick={handleCopy}
												variant={copied ? "default" : "outline"}
												size="lg"
											>
												{copied ? (
													<CheckCircle
														className="w-4 h-4 me-2"
														aria-hidden="true"
													/>
												) : (
													<Copy
														className="w-4 h-4 me-2"
														aria-hidden="true"
													/>
												)}
												{copied
													? t("result.copied")
													: t("result.copy")}
											</Button>
										</div>
									</div>
								) : (
									<div className="text-center min-h-[8rem] flex items-center justify-center">
										<p className="text-muted-foreground font-bold">
											{t("result.empty")}
										</p>
									</div>
								)}
							</CardContent>
						</Card>
					</div>

					{/* Privacy Notice */}
					<Alert className="mt-8">
						<AlertTriangle
							className="h-4 w-4 text-green-600"
							aria-hidden="true"
						/>
						<AlertDescription className="font-bold">
							{t.rich("privacy", {
								code: (chunks) => (
									<code className="bg-muted p-1 rounded font-mono text-sm">
										{chunks}
									</code>
								),
							})}
						</AlertDescription>
					</Alert>
				</div>
			</div>
		</ErrorBoundary>
	);
};

export default PasswordGenerator;
